import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

import Auth from './components/Auth';
import TopNav from './components/TopNav';
import Dashboard from './components/Dashboard';
import MyMetrics from './components/MyMetrics';
import UploadData from './components/UploadData';
import AIAdvisor from './components/AIAdvisor';
import UserProfile from './components/UserProfile';
import Notifications from './components/Notifications';
import Wearables from './components/Wearables';
import HRDashboard from './components/HRDashboard';
import InterventionBooking from './components/InterventionBooking';
import LevareAdmin from './components/LevareAdmin';
import Onboarding from './components/Onboarding';
import Training from './components/Training';
import Nutrition from './components/Nutrition';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsConditions from './components/TermsConditions';

const LEVARE_ADMIN_EMAIL = 'contact@levarewellbeing.com';

const EMPLOYEE_TABS = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'health', label: 'My Health' },
  { id: 'training', label: 'Training' },
  { id: 'nutrition', label: 'Nutrition' },
  { id: 'advisor', label: 'Rico' },
  { id: 'profile', label: 'Profile' },
];

const HR_TABS = [
  { id: 'hr', label: 'Team Dashboard' },
  { id: 'dashboard', label: 'My Dashboard' },
  { id: 'health', label: 'My Health' },
  { id: 'advisor', label: 'Rico' },
  { id: 'profile', label: 'Profile' },
  { id: 'interventions', label: 'Book Intervention' },
];

const ADMIN_TABS = [
  { id: 'admin', label: 'Levare Admin' },
];

// ── HealthHub — My Health tab (Metrics + Upload) ─────────────────────────────
function HealthHub({ user, initialSubTab, metricsInitialTab, setMetricsInitialTab }) {
  const [subTab, setSubTab] = React.useState(initialSubTab || 'metrics');
  React.useEffect(() => { if (initialSubTab) setSubTab(initialSubTab); }, [initialSubTab]);
  const TABS = [
    { id: 'metrics', label: 'My Metrics' },
    { id: 'upload', label: 'Upload Data' },
    { id: 'wearables', label: 'Wearables' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: '.3rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border2)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ padding: '.5rem 1rem', fontSize: '.82rem', color: subTab === t.id ? 'var(--primary)' : 'var(--text3)', cursor: 'pointer', border: 'none', background: 'none', borderBottom: subTab === t.id ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', fontFamily: 'var(--font-body)', fontWeight: subTab === t.id ? 500 : 400, transition: 'color .15s' }}>
            {t.label}
          </button>
        ))}
      </div>
      {subTab === 'metrics' && <MyMetrics user={user} initialTab={metricsInitialTab} onTabSet={() => setMetricsInitialTab(0)} />}
      {subTab === 'upload' && <UploadData user={user} />}
      {subTab === 'wearables' && <Wearables user={user} />}
    </div>
  );
}

// ── ProfileHub — Profile tab (Profile + Notifications + Programmes) ───────────
function ProfileHub({ user, profile, onUpdate }) {
  const [subTab, setSubTab] = useState('profile');
  const TABS = [
    { id: 'profile', label: 'My Profile' },
    { id: 'notifications', label: 'Notifications' },
    { id: 'programmes', label: 'My Programmes' },
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: '.3rem', marginBottom: '1.5rem', borderBottom: '1px solid var(--border2)' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} style={{ padding: '.5rem 1rem', fontSize: '.82rem', color: subTab === t.id ? 'var(--primary)' : 'var(--text3)', cursor: 'pointer', border: 'none', background: 'none', borderBottom: subTab === t.id ? '2px solid var(--primary)' : '2px solid transparent', marginBottom: '-1px', fontFamily: 'var(--font-body)', fontWeight: subTab === t.id ? 500 : 400, transition: 'color .15s' }}>
            {t.label}
          </button>
        ))}
      </div>
      {subTab === 'profile' && <UserProfile user={user} profile={profile} onUpdate={onUpdate} />}
      {subTab === 'notifications' && <Notifications user={user} />}
      {subTab === 'programmes' && <InterventionBooking user={user} profile={profile} />}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [metricsInitialTab, setMetricsInitialTab] = useState(0);
  // ── Theme injection ───────────────────────────────────────────────────────
  React.useEffect(() => {
    // Apply saved theme or default to light
    const saved = localStorage.getItem('levare-app-theme');
    const theme = saved || 'light';
    document.documentElement.setAttribute('data-theme', theme);

    const style = document.createElement('style');
    style.id = 'levare-theme-css';
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');

      /* ── LIGHT THEME (default) ── */
      :root, [data-theme="light"] {
        --bg: #f5f7fa;
        --bg-card: #ffffff;
        --bg-elevated: #eef1f6;
        --primary: #00AEEF;
        --primary-dim: rgba(0,174,239,0.1);
        --success: #059669;
        --success-dim: rgba(5,150,105,0.1);
        --warning: #d97706;
        --warning-dim: rgba(217,119,6,0.1);
        --danger: #dc2626;
        --danger-dim: rgba(220,38,38,0.08);
        --text: #0f172a;
        --text2: #1e3a5f;
        --text3: #64748b;
        --border2: rgba(0,0,0,0.08);
        --nav-bg: rgba(255,255,255,0.97);
        --nav-border: rgba(0,0,0,0.08);
        --font-head: 'Playfair Display', serif;
        --font-body: 'DM Sans', sans-serif;
        --radius: 6px;
        --radius-lg: 12px;
        --shadow-card: 0 1px 4px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04);
        --shadow-nav: 0 1px 0 rgba(0,0,0,0.08);
      }

      /* ── DARK THEME (gold accent) ── */
      [data-theme="dark"] {
        --bg: #0c0c0e;
        --bg-card: #141418;
        --bg-elevated: #1c1c22;
        --primary: #c8a97e;
        --primary-dim: rgba(200,169,126,0.12);
        --success: #4ade80;
        --success-dim: rgba(74,222,128,0.1);
        --warning: #fbbf24;
        --warning-dim: rgba(251,191,36,0.1);
        --danger: #f87171;
        --danger-dim: rgba(248,113,113,0.1);
        --text: #f0ede8;
        --text2: #c8c4bc;
        --text3: #7c7870;
        --border2: rgba(255,255,255,0.07);
        --nav-bg: rgba(12,12,14,0.97);
        --nav-border: rgba(255,255,255,0.06);
        --font-head: 'Playfair Display', serif;
        --font-body: 'DM Sans', sans-serif;
        --radius: 6px;
        --radius-lg: 12px;
        --shadow-card: 0 1px 4px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.3);
        --shadow-nav: 0 1px 0 rgba(255,255,255,0.05);
      }

      /* ── Base ── */
      * { box-sizing: border-box; }
      body {
        margin: 0;
        background: var(--bg);
        color: var(--text);
        font-family: var(--font-body);
        transition: background 0.25s ease, color 0.25s ease;
      }
      img, svg { max-width: 100%; }

      /* ── Scrollbar suppression ── */
      .levare-scroll-hide::-webkit-scrollbar { display: none; }
      .levare-scroll-hide { -ms-overflow-style: none; scrollbar-width: none; }

      /* ── Nav ── */
      nav {
        background: var(--nav-bg) !important;
        border-bottom: 1px solid var(--nav-border) !important;
        backdrop-filter: blur(12px);
      }

      /* ── Card elevation ── */
      .levare-card {
        background: var(--bg-card);
        border: 1px solid var(--border2);
        border-radius: var(--radius-lg);
        box-shadow: var(--shadow-card);
        transition: background 0.25s ease, border-color 0.25s ease;
      }

      /* ── Inputs & selects ── */
      input, select, textarea {
        background: var(--bg-elevated) !important;
        border: 1px solid var(--border2) !important;
        color: var(--text) !important;
        font-family: var(--font-body) !important;
        transition: background 0.25s ease;
      }
      input::placeholder, textarea::placeholder { color: var(--text3) !important; }

      /* ── Buttons base ── */
      button { font-family: var(--font-body); }

      /* ── Role colours ── */
      .role-employee { color: #00c896 !important; }
      .role-hr { color: #00AEEF !important; }
      .role-admin { color: #f0a050 !important; }

      /* ── Health score ring colour (adapts to dark) ── */
      [data-theme="dark"] .score-ring-ok { stroke: #4ade80; }
      [data-theme="dark"] .score-ring-warn { stroke: #fbbf24; }
      [data-theme="dark"] .score-ring-bad { stroke: #f87171; }

      /* ── Mobile breakpoint: 768px ── */
      @media (max-width: 768px) {
        .levare-page { padding: .75rem .75rem 5rem !important; }
        .dash-score-grid { grid-template-columns: 1fr !important; }
        .dash-metrics-grid { grid-template-columns: 1fr 1fr !important; }
        .dash-progress-grid { grid-template-columns: 1fr !important; }
        .dash-trend-grid { grid-template-columns: 1fr !important; }
        .dash-actions { grid-template-columns: 1fr 1fr !important; }
        .levare-2col { grid-template-columns: 1fr !important; }
        .levare-3col { grid-template-columns: 1fr 1fr !important; }
        .levare-4col { grid-template-columns: 1fr 1fr !important; }
        .hr-radar-grid { grid-template-columns: 1fr !important; }
        .hr-risk-grid { grid-template-columns: 1fr !important; }
      }

      /* ── Nav mobile tweaks ── */
      @media (max-width: 640px) {
        .nav-role-badge { display: none !important; }
        .nav-user-name { display: none !important; }
        .nav-user-btn { padding: 0.3rem 0.4rem !important; gap: 0 !important; }
        .nav-chevron { display: none !important; }
      }

      /* ── Small mobile: 480px ── */
      @media (max-width: 480px) {
        .levare-page { padding: .5rem .5rem 5rem !important; }
        .dash-metrics-grid { grid-template-columns: 1fr !important; }
        .levare-3col { grid-template-columns: 1fr !important; }
        .levare-4col { grid-template-columns: 1fr 1fr !important; }
        .dash-actions { grid-template-columns: 1fr 1fr !important; }
        h1 { font-size: 1.25rem !important; }
        h2 { font-size: 1.1rem !important; }
      }

      /* ── Spin animation ── */
      @keyframes spin { to { transform: rotate(360deg); } }
      @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
    `;
    document.head.appendChild(style);
    return () => { const el = document.getElementById('levare-theme-css'); if (el) el.remove(); };
  }, []);

  const [healthSubTab, setHealthSubTab] = useState('metrics');
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadProfile(firebaseUser);
      } else {
        setUser(null);
        setProfile(null);
        setLoading(false);
      }
    });
    return () => unsub();
  }, []);

  const loadProfile = async (firebaseUser) => {
    try {
      const snap = await getDoc(doc(db, 'users', firebaseUser.uid, 'latest', 'profile'));
      if (snap.exists()) {
        const data = snap.data();

        // Security: double-check admin role matches whitelisted email
        if (data.role === 'admin' && firebaseUser.email.toLowerCase() !== LEVARE_ADMIN_EMAIL) {
          await signOut(auth);
          setLoading(false);
          return;
        }

        setProfile(data);

        // Set default tab based on role
        if (data.role === 'admin') {
          setActiveTab('admin');
        } else if (data.role === 'hr') {
          setActiveTab('dashboard');
        } else {
          setActiveTab('dashboard');
        }

        // Show onboarding for new employees/hr
        if (!data.onboardingComplete && data.role !== 'admin') {
          setShowOnboarding(true);
        }
      } else {
        // No profile found yet — reload page after short delay to let registration complete
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      if (err.code === 'permission-denied') {
        setTimeout(() => window.location.reload(), 2000);
        return;
      }
      await signOut(auth);
    }
    setLoading(false);
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setUser(null);
    setProfile(null);
    setActiveTab('dashboard');
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setProfile(prev => ({ ...prev, onboardingComplete: true }));
  };

  const getTabs = () => {
    if (!profile) return [];
    if (profile.role === 'admin') return ADMIN_TABS;
    if (profile.role === 'hr') return HR_TABS;
    return EMPLOYEE_TABS;
  };

  // Guard: ensure tab is valid for this role
  const getSafeTab = (tab, role) => {
    const allowed = getTabs().map(t => t.id);
    if (allowed.includes(tab)) return tab;
    return allowed[0] || 'dashboard';
  };

  const renderTab = () => {
    const role = profile?.role;
    const safeTab = getSafeTab(activeTab, role);

    // Admin-only tabs
    if (safeTab === 'admin' || safeTab === 'companies') {
      if (role !== 'admin') return <AccessDenied />;
      return <LevareAdmin user={user} profile={profile} initialTab={safeTab === 'companies' ? 'companies' : 'overview'} />;
    }

    // HR tabs — accessible by hr and admin
    if (safeTab === 'hr') {
      if (role !== 'hr' && role !== 'admin') return <AccessDenied />;
      return <HRDashboard user={user} profile={profile} />;
    }

    if (safeTab === 'interventions') {
      if (role !== 'hr' && role !== 'admin') return <AccessDenied />;
      return <InterventionBooking user={user} profile={profile} />;
    }

    // Employee tabs — accessible by all roles
    if (safeTab === 'dashboard') return <Dashboard user={user} profile={profile} onTabChange={(tab) => {
      const map = { upload: 'health', metrics: 'health', advisor: 'advisor', wearables: 'health', training: 'training', nutrition: 'nutrition' };
      const subTabMap = { upload: 'upload', metrics: 'metrics', wearables: 'wearables' };
      if (subTabMap[tab]) setHealthSubTab(subTabMap[tab]);
      if (tab === 'assessments') {
        setMetricsInitialTab(4);
        setActiveTab(getSafeTab('metrics', role));
        return;
      }
      setActiveTab(getSafeTab(map[tab] || tab, role));
    }} />;
    if (safeTab === 'health') return <HealthHub user={user} initialSubTab={healthSubTab} metricsInitialTab={metricsInitialTab} setMetricsInitialTab={setMetricsInitialTab} />;
    if (safeTab === 'training') return <Training user={user} />;
    if (safeTab === 'nutrition') return <Nutrition user={user} />;
    if (safeTab === 'advisor') return <AIAdvisor user={user} profile={profile} />;
    if (safeTab === 'profile') return <ProfileHub user={user} profile={profile} onUpdate={p => setProfile(p)} />;

    return <AccessDenied />;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.6rem', color: 'var(--text)', marginBottom: '1.25rem', letterSpacing: '0.02em' }}>
            Levare<span style={{ color: 'var(--primary)' }}>.</span>
          </div>
          <div style={{ width: 32, height: 32, border: '2px solid var(--border2)', borderTop: '2px solid var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  // Privacy policy accessible without login
  if (window.location.hash === '#terms') return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "'DM Sans', sans-serif", padding: '2rem' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: 'var(--text)' }}>Levare<span style={{ color: '#00AEEF' }}>.</span></div>
          <a href="/" style={{ fontSize: '.8rem', color: 'var(--text3)', textDecoration: 'none' }}>← Back to platform</a>
        </div>
        <TermsConditions />
      </div>
    </div>
  );

  if (window.location.hash === '#privacy') return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "'DM Sans', sans-serif", padding: '2rem' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem' }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.2rem', color: 'var(--text)' }}>Levare<span style={{ color: '#00AEEF' }}>.</span></div>
          <a href="/" style={{ fontSize: '.8rem', color: 'var(--text3)', textDecoration: 'none' }}>← Back to platform</a>
        </div>
        <PrivacyPolicy />
      </div>
    </div>
  );

  if (!user || !profile) return <Auth />;

  if (showOnboarding) {
    return <Onboarding user={user} profile={profile} onComplete={handleOnboardingComplete} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "'DM Sans', sans-serif" }}>
      <TopNav
        profile={profile}
        tabs={getTabs()}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(getSafeTab(tab, profile.role))}
        onSignOut={handleSignOut}
      />
      <div style={{ paddingTop: '64px', background: 'var(--bg)', minHeight: '100vh' }}>
        <div className="levare-page" style={{ padding: '1.5rem 1.5rem 3rem', maxWidth: 1200, margin: '0 auto' }}>
          {renderTab()}
          <div style={{ marginTop: '4rem', paddingTop: '1.5rem', borderTop: '0.5px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'center', gap: '2rem' }}>
            <a href="#privacy" style={{ fontSize: '.72rem', color: 'var(--text3)', textDecoration: 'none', fontWeight: 300 }} onClick={() => window.location.hash = 'privacy'}>Privacy Policy</a>
            <a href="#terms" style={{ fontSize: '.72rem', color: 'var(--text3)', textDecoration: 'none', fontWeight: 300 }} onClick={() => window.location.hash = 'terms'}>Terms & Conditions</a>
            <a href="mailto:contact@levarewellbeing.com" style={{ fontSize: '.72rem', color: 'var(--text3)', textDecoration: 'none', fontWeight: 300 }}>contact@levarewellbeing.com</a>
            <span style={{ fontSize: '.72rem', color: 'var(--text3)', fontWeight: 300 }}>© 2026 Levare Wellbeing</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccessDenied() {
  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', color: '#e8f0f8' }}>Access Denied</div>
      <p style={{ color: '#7a9ab8', fontSize: '0.9rem' }}>You don't have permission to view this page.</p>
    </div>
  );
}
